/**
 * Share links — generate and validate shareable audit URLs.
 *
 * Phase 1 approach: encode the job ID into a short URL-safe token.
 * Since fetchJob() already works without auth (the API returns public
 * data for completed jobs), we can use the job ID directly as the
 * share token. The /s/[token] route renders a read-only canvas.
 *
 * Phase 2 will add: expiry, access tracking, revocation, and a
 * backend-native share token with its own table.
 */

// --------------- types ---------------

export interface ShareLink {
  /** The full shareable URL */
  url: string;
  /** Just the token portion */
  token: string;
  /** Job ID this links to */
  jobId: string;
}

// --------------- public API ---------------

/** Generate a share link for an audit. */
export function createShareLink(jobId: string): ShareLink {
  const token = jobId; // Phase 1: token = jobId
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://snapsiteux.com";
  return {
    url: `${baseUrl}/s/${token}`,
    token,
    jobId,
  };
}

/** Extract job ID from a share token. */
export function resolveShareToken(token: string): string {
  return token; // Phase 1: token = jobId
}

/** Copy a share link to clipboard. Returns true on success. */
export async function copyShareLink(jobId: string): Promise<boolean> {
  const { url } = createShareLink(jobId);
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    // Fallback for older browsers
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      // `.remove()` is a no-op if the node was already detached; the older
      // `document.body.removeChild(textarea)` throws "not a child of this
      // node" if anything (a re-render, an extension) moved it first.
      textarea.remove();
      return true;
    } catch {
      return false;
    }
  }
}
